(ns ycweb.app.dashboard.planning.orderoverview
  (:require
   [rum.core :as rum]
   [util.transform :as trf]
   [ycweb.app.admin.wholesale.common :as w.cmn]
   [ycweb.app.dashboard.planning.common :as planning.cmn]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.components.datatables.datatables :as datatables]
   [ycweb.components.flow :as flow]
   [ycweb.components.forms :as forms]
   [ycweb.components.grid :as grid]
   [ycweb.components.headings :as headings]
   [ycweb.components.toaster :as toaster]
   [ycweb.components.transitions :as t]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as u]
   [ycweb.util.urlstate :as urlstate]))

(def ^:private common-order-columns
  [{:key :supplier_id :title "Min Order Qty"
    :render
    {:logic-k :moq-fn
     :render-k :moq-view}}
   {:key :units :title "Units"}
   {:key :supplier_id :title "Min Order Value"
    :type :currency
    :render
    {:logic-k :mov-fn}}
   {:key :cost :title "Cost" :type :tocurrency}
   {:key :split-orders :title "Split Order"
    :render {:render-k :split-order-indicator}}
   {:key :qty :title "MOV/MOQ Grouping"
    :render
    {:render-k :mov-moq-grouping}}])

(def ^:private ready-status-column
  {:key :supplier_id :title "Ready Status"
   :align-class :u-text-center
   :render {:render-k :supplier-ready-toggle}})

(def supplier-order-overview-table-config
  {:table-name :admin.planning-new/order-overview
   :row-id :order-group
   :row-class-k :row-error-fn
   :data-source {:cursor [:admin.planning-new :order-overview]}
   :options
   {:order :asc
    :sticky-header? true
    :sort-title "Supplier"}
   :column-defs
   (into
     [ready-status-column
      {:key :supplier_id :title "Supplier"
       :render
       {:logic-k :supplier-name-fn
        :render-k :supplier-name-with-link}}
      {:key :categories :title "Categories"
       :render {:render-k :category-list}}]
     common-order-columns)})

(def placed-stock-orders-table-config
  {:table-name :admin.planning-new/placed-stock-orders
   :row-id :stockorder_id
   :data-source {:cursor [:admin.planning-new :placed-stock-orders]}
   :options
   {:order :desc
    :sort-title "YCS"
    :paging? false
    :sticky-header? true}
   :column-defs
   [{:key :companyname :title "Supplier"}
    {:key :type :title "Type"}
    {:key :qtyplaced :title "Qty Placed" :type :number}
    {:key :qty :title "Qty Now" :type :number}
    {:key :costplaced :title "Cost Placed (ex VAT)" :type :tocurrency}
    {:key :cost :title "Cost Now (ex VAT)" :type :tocurrency}
    {:key :stockorder_id :title "YCS"
     :align-class :u-text-center
     :render {:render-k :stockorder-link}}]})

(def placed-stock-orders-table-fns
  {:column-fns
   {:stockorder-link
    (fn [_ row _ cell-val]
      [:a
       {:href (str "wholesale.htm?action=stockorder_overview&uuid=" (:uuid row))
        :target "_blank"}
       (str "YCS" cell-val)])}})

(def category-order-overview-table-config
  {:table-name :admin.planning-new/order-overview-categories
   :row-id :order-group
   :row-class-k :row-error-fn
   :data-source {:cursor [:admin.planning-new :order-overview]}
   :options
   {:order :asc
    :sticky-header? true
    :sort-title "Supplier"}
   :column-defs
   (into
     [ready-status-column
      {:key :supplier_id :title "Supplier"
       :render
       {:logic-k :supplier-name-fn
        :render-k :supplier-name-with-link}}
      {:key :cat1 :title "Level 1"}
      {:key :cat2 :title "Level 2"
       :render
       {:render-k :category-name-with-link}}
      {:key :cat3 :title "Level 3"}
      {:key :cat4 :title "Level 4"}]
     common-order-columns)})

(defn plan-supplier [app-atom supplier-id]
  (let [suppliers-idx (u/index-by :id (cmn/curact (cmn/process-cursor app-atom) [:suppliers]))]
    (get suppliers-idx supplier-id)))

(defn supplier-status [app-atom supplier-id]
  (or (:status (plan-supplier app-atom supplier-id)) "Not Ready"))

(defn supplier-status-title [status readiness]
  (cond
    (= "Ordered" status) "Ordered"
    (= :ordered readiness) "Ordered. Click to mark Not Ready"
    (= "Ready" status) "Ready. Click to mark Not Ready"
    :else "Not Ready: Place Orders will skip this supplier. Click to mark Ready"))

(defn not-ready-suppliers
  "Suppliers with rows on the overview that Place Orders would skip."
  [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        supplier-ids (set (map :supplier_id (vals (cmn/curact process-atom :order-overview))))]
    (->>
      (cmn/curact process-atom [:suppliers])
      (filter #(and (contains? supplier-ids (:id %)) (not= "Ready" (:status %)) (not= "Ordered" (:status %))))
      (map :companyname)
      sort)))

(def order-overview-table-fns
  {:config-fns
   {:row-error-fn
    (fn [app-atom row]
      (let [process-atom (cmn/process-cursor app-atom)
            order-overview-vals (vals (cmn/curact process-atom :order-overview))
            order-gpd (group-by (juxt :supplier_id :split) order-overview-vals)
            order-lookup [(:supplier_id row) (:split row)]
            suppliers-idx (u/index-by :id (cmn/curact process-atom [:suppliers]))
            supplier-mov (get-in suppliers-idx [(:supplier_id row) :minordervalue])
            supplier-moq (planning.cmn/moq-number (get-in suppliers-idx [(:supplier_id row) :minorderqty]))
            order-cost-sum (reduce + 0 (map (comp trf/fromcurrency :cost) (get order-gpd order-lookup)))
            order-qty-sum (reduce + 0 (map :units (get order-gpd order-lookup)))]

        (when
          (or
            (< order-cost-sum supplier-mov)
            (< order-qty-sum supplier-moq))
          ["tablesorter-errorRow"])))}

   :column-fns
   {:supplier-name-fn
    (fn [app-atom _ _ cell-val]
      (let [process-atom (cmn/process-cursor app-atom)
            suppliers-atom (rum/cursor-in process-atom [:suppliers])
            suppliers-by-id (u/index-by :id (rum/react suppliers-atom))]
        (get-in suppliers-by-id [cell-val :companyname])))

    ;; The plan overview's supplier toggle, so a skipped supplier can be readied
    ;; from here; the action and its receive handler live in planoverview.
    :supplier-ready-toggle
    (fn [app-atom row _ _]
      (let [{:keys [status sps_id] :as supplier} (plan-supplier app-atom (:supplier_id row))
            status (or status "Not Ready")
            readiness (planning.cmn/supplier-readiness (planning.cmn/supplier-readiness-row app-atom supplier))]
        [:a
         {:class (u/bem :admin-status-dot [(planning.cmn/readiness-dot-modifier readiness) :l])
          :title (supplier-status-title status readiness)
          :href "#"
          :on-click
          (fn [e]
            (.preventDefault e)
            (when (and sps_id (not= status "Ordered"))
              (grid/dispatch-action app-atom :stockorder.planning.supplierplan/update-status
                {:sps-id sps_id})))}]))

    :supplier-name-with-link
    (fn [app-atom row _ cell-val]
      [:a
       (urlstate/href-props app-atom "planning-new" "category"
         {:supplierid (:supplier_id row)
          :planid (:planid (cmn/screen-params app-atom))})
       [:b cell-val]])

    :category-name-with-link
    (fn [app-atom row _ cell-val]
      [:a
       (urlstate/href-props app-atom "planning-new" "category"
         {:cat1 (:level1 row)
          :cat2 (:level2 row)
          :planid (:planid (cmn/screen-params app-atom))})
       cell-val])

    :moq-fn
    (fn [app-atom _ _ cell-val]
      (let [process-atom (cmn/process-cursor app-atom)
            suppliers-atom (rum/cursor-in process-atom [:suppliers])
            suppliers-by-id (u/index-by :id (rum/react suppliers-atom))]
        (get-in suppliers-by-id [cell-val :minorderqty])))

    :mov-fn
    (fn [app-atom _ _ cell-val]
      (let [process-atom (cmn/process-cursor app-atom)
            suppliers-atom (rum/cursor-in process-atom [:suppliers])
            suppliers-by-id (u/index-by :id (rum/react suppliers-atom))]
        (get-in suppliers-by-id [cell-val :minordervalue])))

    :moq-view
    (fn [_ _ _ cell-val]
      [:span
       (cond
         (u/presence cell-val)
         cell-val

         :else
         "N/A")])

    :category-list
    (fn [_ _ _ cell-val]
      (into [:div] (map (fn [category] [:div category]) cell-val)))

    :split-order-indicator
    (fn [app-atom row _ cell-val]
      (let [split-count (or cell-val 0)]
        [:a
         (urlstate/href-props app-atom "planning-new" "category"
           {:supplierid (:supplier_id row)
            :planid (:planid (cmn/screen-params app-atom))})
         (cond
           (pos? split-count)
           (str split-count (if (= 1 split-count) " brand split" " brands split"))

           :else
           "Apply")]))

    :mov-moq-grouping
    (fn [app-atom row _ _]
      (let [process-atom (cmn/process-cursor app-atom)
            order-overview-atom (rum/cursor process-atom :order-overview)]
        (forms/input-raw ""
          (rum/cursor-in order-overview-atom [(:order-group row) :split])
          {:type :number
           :name :mov-moq-group
           :id :mov-moq-group
           :class "text-input--small"
           :style {:width "100%"}})))}})

(rum/defc order-overview < rum/reactive cmn/cursors [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        suppliers (cmn/curact process-atom :suppliers)
        plan-items-atom (rum/cursor-in process-atom [:items])
        plan-items (vals (rum/react plan-items-atom))

        top-level-cats
        (sort-by identity
          (keys (u/index-by (comp first :cats) plan-items)))

        categories-atom (rum/cursor process-atom :categories)
        by-category-atom (rum/cursor process-atom :order-by-category)]

    [:<>
     [:div
      {:class (u/bem :flex-grid-group [:flush :align-left :valign-middle])}
      [:div
       {:class (u/bem :flex-grid-block [:fluid :flush :grow-yes])}
       [:div.flex-grid-content
        (headings/heading "Order Overview" {:type :h2 :size :large})]]
      [:div
       {:class (u/bem :flex-grid-block [:fluid])}
       [:div.flex-grid-content
        (buttons/button-web "Back"
          {:on-click
           #(urlstate/redirect app-atom "planning-new" "plan"
              {:planid (:planid (cmn/screen-params app-atom))})
           :style :secondary})]]
      [:div
       {:class (u/bem :flex-grid-block [:fluid])}
       [:div.flex-grid-content
        (buttons/button-web "Try new layout"
           (merge
             (w.cmn/try-new-layout-props)
             {:style :light
              :size :default}))]]]

     [:form
      [:div.form-field
       [:div {:class (u/bem :flex-grid-group [:flush-force :align-left])}
        [:div {:class (u/bem :flex-grid-block [:fluid])}
         [:div.flex-grid-content
          (forms/select-raw
            (rum/cursor process-atom :group-supplier-id)
            (concat
              [{:text "- Select -" :value nil}]
              (map
                (fn [supplier]
                  {:text (:companyname supplier) :value (:id supplier)})
                suppliers)))
          [:div.form-hint-wrapper
           [:span.form-hint
            (str "Group SKU's per supplier")]]]]
        [:div {:class (u/bem :flex-grid-block [:fluid])}
         [:div.flex-grid-content
          (buttons/button-web "Apply Grouping"
            {:on-click
             (fn [e]
               (.preventDefault e)
               (grid/dispatch-action app-atom :admin.planning-new/order-grouping {}))})]]
        [:div {:class (u/bem :flex-grid-block [:fluid])}
         [:div.flex-grid-content
          (buttons/button-web "Place Orders"
            {:on-click
             (fn [e]
               (.preventDefault e)
               (grid/confirm-modal app-atom
                 {:title "Place Orders"
                  :text
                  [:div
                   [:p "Place the purchase orders for this plan?"]
                   [:p
                    "Each supplier will be emailed their purchase order, sent from
                     the assigned planner and copied to you."]
                   [:p "A supplier with no order email on record will not be sent one."]
                   (when-let [skipped (seq (not-ready-suppliers app-atom))]
                     [:p.plan-supplier-warning
                      (str "Not Ready and will be skipped: " (u/glue ", " skipped)
                        ". Ready them on the plan overview first if they should be ordered.")])]
                  :cancel-text "Cancel"
                  :confirm-text "Place Orders"
                  :confirm-key :admin.planning-new/place-orders}))})]]]]
      [:div.form-field
       (forms/toggle "Break Down By Category" by-category-atom
         {:change-fn #(planning.cmn/setup-order-atom app-atom)})]]

     (into
       [:div {:class (u/bem :flex-grid-group [:align-left :flush :flush-force])}]
       (map
         (fn [category]
           [:div
            {:class (u/bem :flex-grid-block [:fluid])}
            [:div.flex-grid-content
             [:div.form-field
              (forms/checkbox
                category
                (rum/cursor categories-atom category)
                {:change-fn #(planning.cmn/setup-order-atom app-atom)})]]])
         top-level-cats))

     (headings/heading "Orders Placed" {:type :h3 :size :medium})
     (if (seq (cmn/curact process-atom :placed-stock-orders))
       (datatables/table app-atom :admin.planning-new/placed-stock-orders placed-stock-orders-table-fns)
       [:p "No orders have been placed from this plan yet."])

     (headings/heading "Orders To Place" {:type :h3 :size :medium})
     (if (rum/react by-category-atom)
       (datatables/table app-atom
         :admin.planning-new/order-overview-categories
         order-overview-table-fns)
       (datatables/table app-atom
         :admin.planning-new/order-overview
         order-overview-table-fns))]))

(defmethod flow/render-screen :admin.planning-new/order [app-atom _]
  (datatables/initiate app-atom placed-stock-orders-table-config {})
  (datatables/initiate app-atom supplier-order-overview-table-config {})
  (datatables/initiate app-atom category-order-overview-table-config {})
  (planning.cmn/with-loading-overlay app-atom (order-overview app-atom)))

(defmethod t/transition-open :admin.planning-new/order [app-atom _ params]
  (when-not (w.cmn/redirect-to-new-layout-if-needed! app-atom "planning" params)
    (planning.cmn/validate-plan-items app-atom)))

(defmethod grid/action :admin.planning-new/order-grouping [app-atom _ _]
  (let [process-atom (cmn/process-cursor app-atom)
        order-overview-atom (rum/cursor process-atom :order-overview)
        order-overview-vals (vals (cmn/curact process-atom :order-overview))
        group-supplier-id (cmn/curact process-atom :group-supplier-id)

        order-overview-update
        (->>
          order-overview-vals
          (map
            (fn [item]
              (let [split-removed (dissoc item :split)]
                (cond
                  (= (u/parseInt (:supplier_id item)) (u/parseInt group-supplier-id))
                  (assoc split-removed
                    :split 0)

                  :else
                  item))))
          (u/index-by :supplier-cats))]

    (reset! order-overview-atom order-overview-update)))

(defmethod grid/action :admin.planning-new/place-orders [app-atom _ _]
  (let [process-atom (cmn/process-cursor app-atom)
        plan-id (cmn/curact process-atom :planid)
        planner-user-ids (planning.cmn/plan-planner-ids app-atom)]

    (cond
      (empty? planner-user-ids)
      (toaster/open-toast app-atom :error
        "None of this plan's suppliers have a planner assigned." {})

      :else
      (do
        (planning.cmn/show-loading! app-atom "Placing orders")
        (comms/send :admin.planning/stock-orders.create
          {:plan-id plan-id
           :planner-user-ids planner-user-ids})))))

(defmethod comms/receive :admin.planning/stock-orders.create [app-atom {:keys [success orders orders-created msg]}]
  (planning.cmn/hide-loading! app-atom)
  (cond
    (not success)
    (toaster/open-toast app-atom :error (or msg "Could not place the orders.") {})

    :else
    (let [process-atom (cmn/process-cursor app-atom)]
      (grid/close-modal app-atom)
      (toaster/open-toast app-atom :success
        (or msg (str "Placed " orders-created " order(s)."))
        {:closetime 5000})

      ;; The plan items held here still read 'Ready' — dropping them makes the
      ;; next plan screen refetch rather than render the pre-order statuses.
      (swap! process-atom
        #(-> %
           (assoc :placed-orders (u/index-by :stockorder-id orders))
           (dissoc :items)))

      (urlstate/redirect app-atom "planning-new" "ordered"
        {:planid (:planid (cmn/screen-params app-atom))}))))
